import { Modal } from '@abyss/web/ui/Modal/Modal';

import {
  PDF_GENERATING_KEYFRAMES,
  PRINT_STYLES,
  PdfGeneratingContainer,
  PdfGeneratingText,
  PdfSpinner,
} from './PrintDirectoryPage.style';
import type { PrintableProvider, PrintLabels } from './PrintPdfGenerator.types';
import {
  escapeHtml,
  formatDate,
  formatDistance,
  formatPhoneNumber,
  getLocationHeading,
  getProviderPrintContent,
  getSpecialtyHeading,
  hasReferralNeededProviders,
  renderHours,
  renderSideMeta,
  renderSimpleList,
  resolvePdfContentConfig,
  toStringArray,
  writeHtmlToWindow,
} from './PrintPdfGenerator.utils';

export type { PrintableProvider } from './PrintPdfGenerator.types';

export interface PrintPdfGeneratorProps {
  isPdfGenerating: boolean;
  generatingPdfTitle: string;
  generatingPdfMessage: string;
}

const UHC_LOGO_URL =
  'https://abyss-cloud.uhg.com/cdn/assets/v1.5.0/uhc/brandmarks/uhc/uhc-lockup-blue.svg';

const PRINT_WINDOW_DELAY_MS = 250;
const CLOSE_PRINT_WINDOW_DELAY_MS = 1000;
const DEFAULT_PRINT_WINDOW_TITLE = 'Preparing directory...';
const DEFAULT_PRINT_ERROR_MESSAGE = 'Unknown print error';
const DEFAULT_PRINT_LOADING_MESSAGE = 'Preparing provider directory...';
const FALLBACK_LOCATION_HEADING = 'OTHER LOCATIONS';

const buildCoverCopyMarkup = (labels: PrintLabels): string => {
  const content = resolvePdfContentConfig(labels.content);

  return `
  ${content.introText.map(
    (paragraph) => `<p>${escapeHtml(paragraph)}</p>`
  ).join('')}
  <div class="cover-list-title">Visit myuhc.com for:</div>
  <ul class="cover-list">
    ${content.resourceItems.map(
    (item) => `<li>${escapeHtml(item)}</li>`
  ).join('')}
  </ul>
  <p>${escapeHtml(content.memberServicesText)}</p>
  <p>${escapeHtml(
    content.premiumText
  ).replace(
    escapeHtml(content.premiumEmphasisText),
    `<span class="cover-strong">${escapeHtml(
      content.premiumEmphasisText
    )}</span>`
  )}</p>
  <p>${escapeHtml(content.hospitalText)}</p>
  <p>${escapeHtml(content.tierText)}</p>
  <div class="cover-footer-rule"></div>
`;
};

const buildCoverPageMarkup = (
  directoryName: string,
  providerCount: number,
  location: string,
  radius: string,
  labels?: PrintLabels,
  planName?: string
): string => {
  const resultEntityLabel = labels?.resultEntityLabel || 'Providers';

  return `
  <section class="cover-page">
    <div class="cover-header">
      <img
        alt="UnitedHealthcare"
        class="cover-logo"
        src="${UHC_LOGO_URL}"
      />
      <div class="cover-date">${escapeHtml(formatDate())}</div>
    </div>
    <div class="cover-title">${escapeHtml(directoryName)}</div>
    <div class="cover-summary-row">
      <div class="cover-summary">Directory Includes <span class="cover-strong">${providerCount} In-Network ${escapeHtml(
    resultEntityLabel
  )}</span> found within ${escapeHtml(
  radius
)} miles of ${escapeHtml(location)}</div>
      ${planName ? `<div class="cover-plan">${escapeHtml(planName)}</div>` : ''}
    </div>
    <div class="cover-copy">${buildCoverCopyMarkup(labels || { pdfTitle: directoryName })}</div>
  </section>
`;
};

const buildReferralNoteMarkup = (labels: PrintLabels): string => `
  <section class="directory-referral-note">
    <div class="directory-referral-note__rule"></div>
    <p><span class="provider-meta-label">${escapeHtml(
  `${labels.referralNeededLabel || 'Referral Needed'}:`
)}</span> ${escapeHtml(resolvePdfContentConfig(labels.content).referralNoteText)}</p>
  </section>
`;

const buildFooterPageMarkup = (
  labels: PrintLabels,
  showTopDivider: boolean
): string => {
  const content = resolvePdfContentConfig(labels.content);
  const footerClassName = showTopDivider
    ? 'directory-footer-page directory-footer-page--with-divider'
    : 'directory-footer-page';

  return `
  <section class="${footerClassName}">
    ${content.footerText.map(
    (paragraph) => `<p>${escapeHtml(paragraph)}</p>`
  ).join('')}
  </section>
`;
};

const buildProviderMarkup = (
  provider: PrintableProvider,
  labels: PrintLabels
): string => {
  const accessibility = toStringArray(provider.accessibility).join(', ');
  const content = getProviderPrintContent(provider, labels);
  const providerGroupLabel = labels.providerGroupLabel || 'Provider Group';
  const hospitalAffiliationsLabel =
    labels.hospitalAffiliationsLabel || 'Hospital Affiliations';
  const boardCertificationsLabel =
    labels.boardCertificationsLabel || 'Board Certifications';
  const acceptingNewPatientsLabel =
    labels.acceptingNewPatientsLabel || 'Accepting new patients';
  const accessibilityLabel = labels.accessibilityLabel || 'Accessibility';
  const medicalStaffLabel = labels.medicalStaffLabel || 'Medical Staff';
  const virtualVisitOnlyLabel =
    labels.virtualVisitOnlyLabel || 'Virtual Visit Only';
  const ttyLabel = labels.ttyLabel || 'TTY';

  return `
    <article class="provider-card">
      <div class="provider-top-divider"></div>
      <div class="provider-header">
        <span class="provider-name">${escapeHtml(content.nameLine)}</span>
        ${content.identityLine
      ? `<span class="provider-header-meta">${escapeHtml(
        content.identityLine
      )}</span>`
      : ''
    }
      </div>
      ${content.allSpecialties
      ? `<div class="provider-subtitle">${escapeHtml(
        content.allSpecialties
      )}</div>`
      : ''
    }
      ${content.hospitalAffiliations
      ? `<div class="provider-subtitle-meta"><span class="provider-meta-label">${escapeHtml(
        `${hospitalAffiliationsLabel}:`
      )}</span> ${escapeHtml(content.hospitalAffiliations)}</div>`
      : ''
    }
      ${content.boardCertifications
      ? `<div class="provider-subtitle-meta"><span class="provider-meta-label">${escapeHtml(
        `${boardCertificationsLabel}:`
      )}</span> ${escapeHtml(content.boardCertifications)}</div>`
      : ''
    }
      <div class="provider-columns">
        <div class="provider-column provider-column-primary">
          <div class="provider-address">
            ${content.addressLines
      .map((line) => `<div>${escapeHtml(line)}</div>`)
      .join('')}
          </div>
          ${provider.distance !== undefined
      ? `<div class="provider-distance">${escapeHtml(
        formatDistance(provider.distance)
      )}</div>`
      : ''
    }
          ${content.providerGroups
      ? `<div class="provider-strong-line"><span class="provider-inline-label">${escapeHtml(
        `${providerGroupLabel}:`
      )}</span> ${escapeHtml(
        content.providerGroups
      )}</div>`
      : ''
    }
          ${provider.providerId
      ? `<div class="provider-id-line">${escapeHtml(
        `ID: ${provider.providerId}`
      )}</div>`
      : ''
    }
          ${content.isVirtualVisitOnly
      ? `<div class="provider-strong-line">${escapeHtml(
        virtualVisitOnlyLabel
      )}</div>`
      : ''
    }
        </div>
        <div class="provider-column provider-column-contact">
          ${renderSimpleList(content.phones, 'provider-phone-line')}
          ${provider.tty
      ? `<div class="provider-phone-line">${escapeHtml(
        `${formatPhoneNumber(provider.tty)} ${ttyLabel}`
      )}</div>`
      : ''
    }
          ${renderHours(
      provider.hoursOfOperation,
      labels.dayAbbreviations,
      labels.closedLabel || 'Closed'
    )}
        </div>
        <div class="provider-column provider-column-status">
          ${renderSimpleList(content.appointmentLines, 'provider-status-line')}
        </div>
        <div class="provider-column provider-column-details">
          ${provider.acceptingNewPatients
      ? `<div class="provider-status-line">${escapeHtml(
        acceptingNewPatientsLabel
      )}</div>`
      : ''
    }
          ${content.referralIndicator
      ? `<div class="provider-side-row">${escapeHtml(
        content.referralIndicator
      )}</div>`
      : ''
    }
          ${content.practiceLimit
      ? `<div class="provider-side-row">${escapeHtml(
        content.practiceLimit
      )}</div>`
      : ''
    }
          ${renderSideMeta(`${accessibilityLabel}:`, accessibility)}
          ${renderSideMeta(`${medicalStaffLabel}:`, content.medicalStaff)}
        </div>
      </div>
    </article>
  `;
};

const buildDirectorySections = (
  providers: PrintableProvider[],
  labels: PrintLabels
): string => {
  const sections = new Map<string, Map<string, PrintableProvider[]>>();
  const defaultSectionLabel = (
    labels.resultEntityLabel || 'Providers'
  ).toUpperCase();

  providers.forEach((provider) => {
    const locationHeading =
      getLocationHeading(provider) || FALLBACK_LOCATION_HEADING;
    const specialtyHeading = getSpecialtyHeading(provider) || defaultSectionLabel;

    if (!sections.has(locationHeading)) {
      sections.set(locationHeading, new Map<string, PrintableProvider[]>());
    }

    const specialtyMap = sections.get(locationHeading)!;
    if (!specialtyMap.has(specialtyHeading)) {
      specialtyMap.set(specialtyHeading, []);
    }

    specialtyMap.get(specialtyHeading)!.push(provider);
  });

  return Array.from(sections.entries())
    .map(
      ([locationHeading, specialtyMap]) => `
        <section class="directory-section">
          <div class="section-location">${escapeHtml(locationHeading)}</div>
          ${Array.from(specialtyMap.entries())
          .map(
            ([specialtyHeading, groupedProviders]) => `
                <div class="section-specialty">${escapeHtml(
              specialtyHeading
            )}</div>
                ${groupedProviders
                .map((provider) => buildProviderMarkup(provider, labels))
                .join('')}
              `
          )
          .join('')}
        </section>
      `
    )
    .join('');
};

export const buildProviderDirectoryHtml = (
  providers: PrintableProvider[],
  location: string,
  radius: string,
  labels: PrintLabels
): string => {
  const directoryName = labels.directoryName || labels.pdfTitle;
  const showReferralNote = hasReferralNeededProviders(providers);

  return `
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${escapeHtml(directoryName)}</title>
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        ${buildCoverPageMarkup(
    directoryName,
    providers.length,
    location,
    radius,
    labels,
    labels.planName
  )}
        ${buildDirectorySections(providers, labels)}
        ${showReferralNote ? buildReferralNoteMarkup(labels) : ''}
        ${buildFooterPageMarkup(labels, !showReferralNote)}
        <script>
          window.onload = () => {
            window.setTimeout(() => {
              window.print();
              window.setTimeout(() => window.close(), ${CLOSE_PRINT_WINDOW_DELAY_MS});
            }, ${PRINT_WINDOW_DELAY_MS});
          };
        </script>
      </body>
    </html>
  `;
};

export const PrintPdfGenerator = (props: PrintPdfGeneratorProps) => {
  const { isPdfGenerating, generatingPdfTitle, generatingPdfMessage } = props;
  if (!isPdfGenerating) return null;

  return (
    <>
      <style>{PDF_GENERATING_KEYFRAMES}</style>
      <Modal
        data-testid="pdf-generating-modal"
        isOpen
        onClose={() => { }}
        title={generatingPdfTitle || 'Generating PDF...'}
      >
        <PdfGeneratingContainer>
          <PdfSpinner />
          <PdfGeneratingText>{generatingPdfMessage}</PdfGeneratingText>
        </PdfGeneratingContainer>
      </Modal>
    </>
  );
};

export const usePrintPdf = () => {
  const showPrintError = (
    printWindow: Window | null,
    message: string = DEFAULT_PRINT_ERROR_MESSAGE
  ) => {
    writeHtmlToWindow(
      printWindow,
      `
      <html>
        <head>
          <title>Print error</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              color: #202124;
            }
            pre {
              white-space: pre-wrap;
              background: #f6f8fa;
              padding: 12px;
              border: 1px solid #d0d7de;
            }
          </style>
        </head>
        <body>
          <h1>Unable to render provider directory</h1>
          <pre>${escapeHtml(message)}</pre>
        </body>
      </html>
    `
    );
  };

  const openPrintWindow = (
    title: string = DEFAULT_PRINT_WINDOW_TITLE
  ): Window | null => {
    if (typeof window === 'undefined') return null;

    const printWindow = window.open('', '', 'width=1200,height=900');
    if (!printWindow) return null;

    writeHtmlToWindow(
      printWindow,
      `
      <html>
        <head>
          <title>${escapeHtml(title)}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              color: #202124;
            }
          </style>
        </head>
        <body>${DEFAULT_PRINT_LOADING_MESSAGE}</body>
      </html>
    `
    );

    return printWindow;
  };

  const printProviderPdf = (
    printWindow: Window | null,
    providers: PrintableProvider[],
    location: string,
    radius: string,
    labels: PrintLabels
  ): void => {
    if (!printWindow) return;

    try {
      const html = buildProviderDirectoryHtml(
        providers || [],
        location,
        radius,
        labels
      );
      writeHtmlToWindow(printWindow, html);
      printWindow.focus();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : DEFAULT_PRINT_ERROR_MESSAGE;
      showPrintError(printWindow, message);
    }
  };

  return { openPrintWindow, printProviderPdf, showPrintError };
};
